import { FormBlock, FormTheme } from './types'
import { COUNTRY_CODES } from './countryPhoneCodes'

// ── Phone Dropdown Script ──
function generatePhoneScript(): string {
  return `
(function(){
  var dropdowns = document.querySelectorAll('.fb-phone-dropdown');
  if (!dropdowns.length) return;
  dropdowns.forEach(function(dd) {
    var toggle = dd.querySelector('.fb-phone-toggle');
    var menu = dd.querySelector('.fb-phone-menu');
    var search = dd.querySelector('.fb-phone-search');
    var list = dd.querySelector('.fb-phone-list');
    var options = dd.querySelectorAll('.fb-phone-option');
    var flagEl = dd.querySelector('.fb-phone-flag');
    var dialEl = dd.querySelector('.fb-phone-dial');
    var wrapper = dd.closest('.fb-phone-wrapper');
    var phoneInput = wrapper ? wrapper.querySelector('.fb-phone-input') : null;
    var hiddenInput = wrapper ? wrapper.querySelector('input[type="hidden"]') : null;
    if (!toggle || !menu) return;

    function updateHidden() {
      if (hiddenInput && dialEl && phoneInput) {
        hiddenInput.value = dialEl.textContent + (phoneInput.value ? ' ' + phoneInput.value : '');
      }
    }

    toggle.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      var isOpen = menu.classList.contains('open');
      // Close all other open menus first
      document.querySelectorAll('.fb-phone-menu.open').forEach(function(m) { m.classList.remove('open'); });
      if (!isOpen) {
        menu.classList.add('open');
        if (search) { search.value = ''; search.focus(); }
        // Reset filter
        options.forEach(function(o) { o.style.display = ''; });
      }
    });

    if (search) {
      search.addEventListener('input', function() {
        var q = search.value.toLowerCase();
        options.forEach(function(o) {
          var name = (o.getAttribute('data-name') || '').toLowerCase();
          var dial = (o.getAttribute('data-dial') || '').toLowerCase();
          o.style.display = (name.indexOf(q) !== -1 || dial.indexOf(q) !== -1) ? '' : 'none';
        });
      });
      // Prevent menu close when typing in search
      search.addEventListener('click', function(e) { e.stopPropagation(); });
    }

    options.forEach(function(opt) {
      opt.addEventListener('click', function(e) {
        e.stopPropagation();
        var iso = opt.getAttribute('data-iso');
        var dial = opt.getAttribute('data-dial');
        var flag = opt.querySelector('.fb-phone-opt-flag');
        if (flagEl && flag) flagEl.textContent = flag.textContent;
        if (dialEl && dial) dialEl.textContent = dial;
        menu.classList.remove('open');
        updateHidden();
        if (phoneInput) phoneInput.focus();
      });
    });

    if (phoneInput) {
      phoneInput.addEventListener('input', updateHidden);
    }
  });

  // Close all menus when clicking outside
  document.addEventListener('click', function(e) {
    if (!e.target.closest('.fb-phone-dropdown')) {
      document.querySelectorAll('.fb-phone-menu.open').forEach(function(m) { m.classList.remove('open'); });
    }
  });
})();`
}

function hasPhoneBlock(blocks: FormBlock[]): boolean {
  for (const b of blocks) {
    if (b.type === 'phone') return true
    if (b.children) {
      if (hasPhoneBlock(b.children)) return true
    }
  }
  return false
}

export function generateFormHtml(
  blocks: FormBlock[],
  theme: FormTheme = DEFAULT_FORM_THEME,
): { html: string; css: string } {
  if (theme.layoutMode === 'card') {
    return generateCardFormHtml(blocks, theme)
  }

  const lines: string[] = []
  let hasPhone = false
  let hasRecaptcha = false

  // 1. Wrap in a container
  lines.push(
    '<div class="fb-form-container" style="max-width: 720px; margin: 0 auto; padding: 32px;">',
  )

  // 2. Add form wrapper
  lines.push('<form class="fb-form" novalidate>')

  let inHalfRow = false

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i]
    const nextBlock = blocks[i + 1]
    const isHalf = block.width === 'half'
    const nextIsHalf = nextBlock?.width === 'half'

    if (isHalf && !inHalfRow) {
      lines.push('  <div class="fb-row">')
      inHalfRow = true
    }

    switch (block.type) {
      case 'phone':
        hasPhone = true
        lines.push(renderPhoneField(block, theme))
        break
      case 'recaptcha':
        hasRecaptcha = true
        lines.push(
          `<div class="form-group" style="margin-bottom: ${theme.fieldSpacing}px;">`,
        )
        if (block.label && !theme.labelColor.includes('transparent')) {
          lines.push(
            `  <label style="display: block; margin-bottom: 6px; font-size: ${theme.labelFontSize}px; font-weight: ${theme.labelFontWeight}; color: ${theme.labelColor};">${block.label}</label>`,
          )
        }
        lines.push(
          `  <div class="g-recaptcha" data-sitekey="${block.recaptchaSiteKey || 'YOUR_SITE_KEY'}" data-theme="${block.recaptchaTheme || 'light'}" data-size="${block.recaptchaSize || 'normal'}"></div>`,
        )
        lines.push(`</div>`)
        break
      case 'submit':
        lines.push(renderSubmitButton(block, theme))
        break
      default:
        lines.push(renderBlock(block))
    }

    if (inHalfRow && (!nextIsHalf || !nextBlock)) {
      lines.push('  </div>')
      inHalfRow = false
    }
  }

  lines.push('</form>')
  lines.push('</div>')

  if (hasPhone) {
    lines.push(
      '<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/css/intlTelInput.css"/>',
    )
    lines.push(
      '<script src="https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/intlTelInput.min.js"></script>',
    )
  }

  if (hasRecaptcha) {
    lines.push(
      '<script src="https://www.google.com/recaptcha/api.js" async defer></script>',
    )
  }

  // Add script for multi-step / conditional logic / phone init
  lines.push('<script>')
  lines.push(`(function(){
  var current = 0;
  var total = ${blocks.length};
  var steps = document.querySelectorAll('.fb-step');
  var bar = document.getElementById('fbProgressBar');
  var counter = document.getElementById('fbStepCounter');
  function show(idx) {
    steps.forEach(function(s, i) {
      s.classList.toggle('fb-step-active', i === idx);
    });
    if (bar) bar.style.width = ((idx + 1) / total * 100) + '%';
    if (counter) counter.textContent = 'Step ' + (idx + 1) + ' of ' + total;
    var active = steps[idx];
    if (active) {
      var inp = active.querySelector('input, select, textarea');
      if (inp) setTimeout(function(){ inp.focus(); }, 150);
    }
  }
  window.fbNext = function() {
    var active = steps[current];
    var required = active.querySelectorAll('[required]');
    for (var i = 0; i < required.length; i++) {
      if (!required[i].value || !required[i].value.trim()) {
        required[i].classList.add('fb-input-error');
        required[i].focus();
        return;
      }
      required[i].classList.remove('fb-input-error');
    }
    if (current < total - 1) { current++; show(current); }
  };
  window.fbPrev = function() {
    if (current > 0) { current--; show(current); }
  };
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Enter') {
      var tag = e.target.tagName;
      if (tag === 'TEXTAREA') return;
      if (tag === 'BUTTON') return;
      e.preventDefault();
      if (current < total - 1) fbNext();
    }
  });
})();`)
  lines.push('</script>')

  // Add phone dropdown script if any phone blocks exist
  if (hasPhoneBlock(blocks)) {
    lines.push('<' + 'script>')
    lines.push(generatePhoneScript())
    lines.push('</' + 'script>')
  }

  const html = lines.join('\n')
  const css = generateFormCss(theme)

  return { html, css }
}

// ── Card Mode Generator ──

interface CardStep {
  decorativeBlocks: FormBlock[] // headings, paragraphs, dividers, spacers, images before the input
  inputBlock: FormBlock | null // the actual input field
}

function generateCardFormHtml(
  blocks: FormBlock[],
  theme: FormTheme,
): { html: string; css: string } {
  // Group blocks into steps
  const steps: CardStep[] = []
  const hiddenBlocks: FormBlock[] = []
  let pendingDecorative: FormBlock[] = []

  const inputTypes = new Set([
    'text',
    'email',
    'phone',
    'number',
    'textarea',
    'url',
    'select',
    'radio',
    'checkbox',
    'consent',
    'date',
    'file',
    'subform',
  ])

  for (const block of blocks) {
    if (block.type === 'hidden') {
      hiddenBlocks.push(block)
      continue
    }
    if (block.type === 'submit') {
      // Submit goes on the last step — attach to pending or last step
      if (steps.length > 0) {
        // We'll handle submit separately
      }
      continue
    }
    if (inputTypes.has(block.type) || block.type === 'section') {
      if (block.type === 'section') {
        // Section title becomes decorative for the next input
        pendingDecorative.push(block)
        continue
      }
      steps.push({
        decorativeBlocks: [...pendingDecorative],
        inputBlock: block,
      })
      pendingDecorative = []
    } else {
      // Decorative block (heading, paragraph, divider, spacer, image)
      pendingDecorative.push(block)
    }
  }

  // If there are trailing decorative blocks with no input, make them a step
  if (pendingDecorative.length > 0) {
    steps.push({ decorativeBlocks: pendingDecorative, inputBlock: null })
  }

  // Find submit block
  const submitBlock = blocks.find((b) => b.type === 'submit')

  if (steps.length === 0) {
    return {
      html: '<form class="fb-form" novalidate></form>',
      css: generateFormCss(theme),
    }
  }

  const totalSteps = steps.length
  const lines: string[] = []

  const showProgress = theme.cardShowProgress !== false
  const showCounter = theme.cardShowCounter !== false

  lines.push('<div class="fb-card-wrapper">')
  if (showProgress) {
    lines.push(
      '  <div class="fb-card-progress"><div class="fb-card-progress-bar" id="fbProgressBar"></div></div>',
    )
  }
  if (showCounter) {
    lines.push(
      '  <div class="fb-card-counter" id="fbStepCounter">Step 1 of ' +
        totalSteps +
        '</div>',
    )
  }
  lines.push('  <form class="fb-form fb-card-mode" novalidate>')

  // Hidden fields
  for (const hb of hiddenBlocks) {
    lines.push('    ' + renderBlock(hb))
  }

  steps.forEach((step, idx) => {
    const isFirst = idx === 0
    const isLast = idx === totalSteps - 1
    const activeClass = isFirst ? ' fb-step-active' : ''

    lines.push(`    <div class="fb-step${activeClass}" data-step="${idx}">`)
    lines.push(`      <div class="fb-step-content">`)

    // Render decorative blocks
    for (const db of step.decorativeBlocks) {
      if (db.type === 'section') {
        lines.push(
          `        <h3 class="fb-heading fb-section-title">${esc(db.sectionTitle || db.label || 'Section')}</h3>`,
        )
      } else {
        lines.push('        ' + renderBlock(db))
      }
    }

    // Render input block
    if (step.inputBlock) {
      lines.push('        ' + renderBlock(step.inputBlock))
    }

    // Render submit on last step
    if (isLast && submitBlock) {
      lines.push('        ' + renderBlock(submitBlock))
    }

    lines.push(`      </div>`)

    // Navigation
    const navStyle = theme.cardNavStyle || 'text'
    const prevLabel =
      navStyle === 'icons' ? '&#8249;' : esc(theme.cardPrevLabel || '← Back')
    const nextLabel =
      navStyle === 'icons' ? '&#8250;' : esc(theme.cardNextLabel || 'Next →')
    const navClass =
      navStyle === 'pills'
        ? ' fb-card-nav-pills'
        : navStyle === 'icons'
          ? ' fb-card-nav-icons'
          : ''

    lines.push(`      <div class="fb-card-nav${navClass}">`)
    if (!isFirst) {
      lines.push(
        `        <button type="button" class="fb-nav-btn fb-nav-prev" onclick="fbPrev()">${prevLabel}</button>`,
      )
    } else {
      lines.push(`        <div></div>`)
    }
    if (!isLast) {
      lines.push(
        `        <button type="button" class="fb-nav-btn fb-nav-next" onclick="fbNext()">${nextLabel}</button>`,
      )
    }
    lines.push(`      </div>`)

    lines.push(`    </div>`)
  })

  lines.push('  </form>')
  lines.push('</div>')

  // Inline JS for navigation
  lines.push('<' + 'script>')
  lines.push(`(function(){
  var current = 0;
  var total = ${totalSteps};
  var steps = document.querySelectorAll('.fb-step');
  var bar = document.getElementById('fbProgressBar');
  var counter = document.getElementById('fbStepCounter');
  function show(idx) {
    steps.forEach(function(s, i) {
      s.classList.toggle('fb-step-active', i === idx);
    });
    if (bar) bar.style.width = ((idx + 1) / total * 100) + '%';
    if (counter) counter.textContent = 'Step ' + (idx + 1) + ' of ' + total;
    var active = steps[idx];
    if (active) {
      var inp = active.querySelector('input, select, textarea');
      if (inp) setTimeout(function(){ inp.focus(); }, 150);
    }
  }
  window.fbNext = function() {
    var active = steps[current];
    var required = active.querySelectorAll('[required]');
    for (var i = 0; i < required.length; i++) {
      if (!required[i].value || !required[i].value.trim()) {
        required[i].classList.add('fb-input-error');
        required[i].focus();
        return;
      }
      required[i].classList.remove('fb-input-error');
    }
    if (current < total - 1) { current++; show(current); }
  };
  window.fbPrev = function() {
    if (current > 0) { current--; show(current); }
  };
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Enter') {
      var tag = e.target.tagName;
      if (tag === 'TEXTAREA') return;
      if (tag === 'BUTTON') return;
      e.preventDefault();
      if (current < total - 1) fbNext();
    }
  });
})();`)
  lines.push('</' + 'script>')

  // Add phone dropdown script if any phone blocks exist
  if (hasPhoneBlock(blocks)) {
    lines.push('<' + 'script>')
    lines.push(generatePhoneScript())
    lines.push('</' + 'script>')
  }

  const html = lines.join('\n')
  const css = generateFormCss(theme)

  return { html, css }
}

function renderBlock(block: FormBlock): string {
  const indent = block.width === 'half' ? '    ' : '  '
  const wrapClass = block.width === 'half' ? 'fb-field fb-half' : 'fb-field'
  const wrapperAttr = `data-block-id="${block.id}"`

  switch (block.type) {
    case 'phone':
      return renderPhoneInput(block, indent, wrapClass, wrapperAttr)
    case 'text':
    case 'email':
    case 'number':
    case 'url':
    case 'date':
      return renderInput(block, indent, wrapClass, wrapperAttr)
    case 'textarea':
      return renderTextarea(block, indent, wrapClass, wrapperAttr)
    case 'select':
      return renderSelect(block, indent, wrapClass, wrapperAttr)
    case 'radio':
      return renderRadioGroup(block, indent, wrapClass, wrapperAttr)
    case 'checkbox':
      return renderCheckboxGroup(block, indent, wrapClass, wrapperAttr)
    case 'consent':
      return renderConsent(block, indent, wrapClass, wrapperAttr)
    case 'file':
      return renderFileInput(block, indent, wrapClass, wrapperAttr)
    case 'hidden':
      return `${indent}<input type="hidden" name="${esc(block.name)}" id="${esc(block.name)}" value="${esc(block.defaultValue)}" ${wrapperAttr} />`
    case 'heading':
      const tag = `h${block.level || 2}`
      return `${indent}<${tag} class="fb-heading" ${wrapperAttr}>${esc(block.content || '')}</${tag}>`
    case 'paragraph':
      return `${indent}<p class="fb-paragraph" ${wrapperAttr}>${esc(block.content || '')}</p>`
    case 'divider':
      return `${indent}<hr class="fb-divider" ${wrapperAttr} />`
    case 'spacer':
      return `${indent}<div class="fb-spacer" style="height:${block.height || 24}px" ${wrapperAttr}></div>`
    case 'video_embed': {
      if (!block.videoUrl) {
        return `${indent}<div class="fb-video-placeholder" style="background:#f3f4f6;border:2px dashed #d1d5db;border-radius:6px;padding:40px;text-align:center;color:#9ca3af;font-size:14px;" ${wrapperAttr}>Video / GIF placeholder</div>`
      }
      const isMp4 = block.videoUrl.toLowerCase().endsWith('.mp4')
      const isGif = block.videoUrl.toLowerCase().endsWith('.gif')
      const isYoutube =
        block.videoUrl.includes('youtube.com') ||
        block.videoUrl.includes('youtu.be')
      const isVimeo = block.videoUrl.includes('vimeo.com')

      if (isGif) {
        return `${indent}<img src="${esc(block.videoUrl)}" alt="GIF" style="width:100%;height:auto;border-radius:6px;display:block;" ${wrapperAttr} />`
      }

      if (isMp4) {
        const attrs = [
          'controls',
          block.videoAutoplay ? 'autoplay' : '',
          block.videoMuted ? 'muted' : '',
          block.videoLoop ? 'loop' : '',
          'playsinline',
        ]
          .filter(Boolean)
          .join(' ')
        return `${indent}<video src="${esc(block.videoUrl)}" ${attrs} style="width:100%;height:auto;border-radius:6px;display:block;" ${wrapperAttr}></video>`
      }

      let embedUrl = block.videoUrl
      if (isYoutube) {
        const videoId = block.videoUrl.match(
          /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?]+)/,
        )?.[1]
        if (videoId) {
          embedUrl = `https://www.youtube.com/embed/${videoId}?rel=0${block.videoAutoplay ? '&autoplay=1' : ''}${block.videoMuted ? '&mute=1' : ''}${block.videoLoop ? `&loop=1&playlist=${videoId}` : ''}`
        }
      } else if (isVimeo) {
        const videoId = block.videoUrl.match(
          /vimeo\.com\/(?:video\/)?([0-9]+)/,
        )?.[1]
        if (videoId) {
          embedUrl = `https://player.vimeo.com/video/${videoId}?title=0&byline=0&portrait=0${block.videoAutoplay ? '&autoplay=1' : ''}${block.videoMuted ? '&muted=1' : ''}${block.videoLoop ? '&loop=1' : ''}`
        }
      }

      const paddingBottom =
        block.videoAspectRatio === '4:3'
          ? '75%'
          : block.videoAspectRatio === '1:1'
            ? '100%'
            : block.videoAspectRatio === '9:16'
              ? '177.77%'
              : '56.25%' // 16:9 default

      return `${indent}<div class="fb-video-wrap" style="position:relative;width:100%;padding-bottom:${paddingBottom};background:#f3f4f6;border-radius:6px;overflow:hidden;" ${wrapperAttr}>
${indent}  <iframe src="${esc(embedUrl)}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
${indent}</div>`
    }
    case 'image': {
      if (!block.src)
        return `${indent}<div class="fb-image-placeholder" ${wrapperAttr}>Image</div>`
      // Build inline styles for image
      const imgStyles: string[] = []
      const wrapStyles: string[] = []
      const sizing = block.imageSizing || 'maxWidth'
      imgStyles.push('display:block')
      if (sizing === 'natural') {
        // No size constraints — use natural dimensions
      } else if (sizing === 'maxWidth') {
        const maxW = block.imageMaxWidth || 100
        imgStyles.push(`max-width:${maxW}%`)
        imgStyles.push('height:auto')
      } else if (sizing === 'custom') {
        if (block.imageWidth) imgStyles.push(`width:${block.imageWidth}px`)
        if (block.imageHeight) imgStyles.push(`height:${block.imageHeight}px`)
        if (block.imageMaxWidth)
          imgStyles.push(`max-width:${block.imageMaxWidth}%`)
      }
      if (block.imageMaxHeight)
        imgStyles.push(`max-height:${block.imageMaxHeight}px`)
      if (block.imageBorderRadius)
        imgStyles.push(`border-radius:${block.imageBorderRadius}px`)
      if (block.imageOpacity != null && block.imageOpacity < 100)
        imgStyles.push(`opacity:${block.imageOpacity / 100}`)
      if (block.imageObjectFit)
        imgStyles.push(`object-fit:${block.imageObjectFit}`)
      // Shadow
      if (block.imageShadow === 'sm')
        imgStyles.push('box-shadow:0 1px 2px 0 rgba(0,0,0,0.05)')
      else if (block.imageShadow === 'md')
        imgStyles.push('box-shadow:0 4px 6px -1px rgba(0,0,0,0.1)')
      else if (block.imageShadow === 'lg')
        imgStyles.push('box-shadow:0 10px 15px -3px rgba(0,0,0,0.1)')
      // Animation class
      const animClass =
        block.imageAnimation && block.imageAnimation !== 'none'
          ? ` fb-img-${block.imageAnimation}`
          : ''
      // Wrapper alignment
      const imgAlign = block.imageAlign || 'center'
      wrapStyles.push(`text-align:${imgAlign}`)
      // Position
      if (block.imagePosition && block.imagePosition !== 'static') {
        wrapStyles.push(`position:${block.imagePosition}`)
        if (block.imageTopOffset != null)
          wrapStyles.push(`top:${block.imageTopOffset}px`)
        if (block.imageLeftOffset != null)
          wrapStyles.push(`left:${block.imageLeftOffset}px`)
        if (block.imagePosition === 'sticky') wrapStyles.push('z-index:10')
        if (block.imagePosition === 'fixed') wrapStyles.push('z-index:1000')
        if (block.imagePosition === 'absolute') wrapStyles.push('z-index:10')
      }
      // Margin for alignment
      if (imgAlign === 'center') imgStyles.push('margin:0 auto')
      else if (imgAlign === 'right') imgStyles.push('margin-left:auto')
      const wrapStyleAttr = wrapStyles.length
        ? ` style="${wrapStyles.join(';')}"`
        : ''
      return `${indent}<div class="fb-image-wrap${animClass}"${wrapStyleAttr} ${wrapperAttr}><img src="${esc(block.src)}" alt="${esc(block.alt || '')}" class="fb-image" style="${imgStyles.join(';')}" /></div>`
    }
    case 'submit': {
      const align = block.buttonAlign || 'full'
      const submitStyles: string[] = []
      const btnInlineStyles: string[] = []
      if (block.buttonColor) {
        btnInlineStyles.push(`background:${esc(block.buttonColor)}`)
        btnInlineStyles.push(`border-color:${esc(block.buttonColor)}`)
      }
      // Position behavior
      if (block.buttonPosition === 'sticky') {
        submitStyles.push('position:sticky')
        submitStyles.push(`bottom:${block.buttonStickyOffset || 0}px`)
        submitStyles.push('z-index:50')
        submitStyles.push('background:inherit')
        submitStyles.push('padding:12px 0')
      } else if (block.buttonPosition === 'fixed') {
        submitStyles.push('position:fixed')
        submitStyles.push(`bottom:${block.buttonStickyOffset || 0}px`)
        submitStyles.push('left:0')
        submitStyles.push('right:0')
        submitStyles.push('z-index:1000')
        submitStyles.push('padding:12px 24px')
        submitStyles.push('background:rgba(255,255,255,0.95)')
        submitStyles.push('backdrop-filter:blur(8px)')
        submitStyles.push('box-shadow:0 -2px 10px rgba(0,0,0,0.1)')
      }
      const submitStyleAttr = submitStyles.length
        ? ` style="${submitStyles.join(';')}"`
        : ''
      const btnStyleAttr = btnInlineStyles.length
        ? ` style="${btnInlineStyles.join(';')}"`
        : ''
      return `${indent}<div class="fb-submit fb-submit-${align}"${submitStyleAttr} ${wrapperAttr}><button type="submit" class="fb-button"${btnStyleAttr}>${esc(block.buttonText || 'Submit')}</button></div>`
    }
    case 'section': {
      const animClass =
        block.sectionAnimation && block.sectionAnimation !== 'none'
          ? ` fb-section-anim fb-sec-${block.sectionAnimation}`
          : ''
      const borderClass =
        block.sectionBorderStyle === 'dashed'
          ? ' fb-section-dashed'
          : block.sectionBorderStyle === 'accent'
            ? ' fb-section-accent'
            : block.sectionBorderStyle === 'none'
              ? ' fb-section-noborder'
              : ''
      const bgStyle = block.sectionBackground
        ? ` style="background:${esc(block.sectionBackground)}"`
        : ''
      const collapsible = block.sectionCollapsible
      const showTitle = block.sectionShowTitle !== false
      const sectionLines: string[] = []
      sectionLines.push(
        `${indent}<div class="fb-section${animClass}${borderClass}"${bgStyle} ${wrapperAttr}>`,
      )
      if (showTitle && block.sectionTitle) {
        if (collapsible) {
          sectionLines.push(
            `${indent}  <div class="fb-section-header fb-section-collapsible" onclick="this.parentElement.classList.toggle('fb-section-collapsed')">`,
          )
          sectionLines.push(
            `${indent}    <span class="fb-section-title">${esc(block.sectionTitle)}</span>`,
          )
          sectionLines.push(
            `${indent}    <span class="fb-section-chevron">▾</span>`,
          )
          sectionLines.push(`${indent}  </div>`)
        } else {
          sectionLines.push(`${indent}  <div class="fb-section-header">`)
          sectionLines.push(
            `${indent}    <span class="fb-section-title">${esc(block.sectionTitle)}</span>`,
          )
          sectionLines.push(`${indent}  </div>`)
        }
      }
      sectionLines.push(`${indent}  <div class="fb-section-body">`)
      if (block.children && block.children.length > 0) {
        for (const child of block.children) {
          sectionLines.push(renderBlock(child))
        }
      }
      sectionLines.push(`${indent}  </div>`)
      sectionLines.push(`${indent}</div>`)
      return sectionLines.join('\n')
    }
    case 'columns': {
      const colCount = block.columnCount || 2
      const gap = block.columnGap || 16
      const cols = block.columns || []
      const colLines: string[] = []

      colLines.push(
        `${indent}<div class="fb-columns" style="--col-count: ${colCount}; --col-gap: ${gap}px;" ${wrapperAttr}>`,
      )

      for (let i = 0; i < colCount; i++) {
        const colBlocks = cols[i] || []
        colLines.push(`${indent}  <div class="fb-col">`)
        for (const child of colBlocks) {
          colLines.push(renderBlock(child))
        }
        colLines.push(`${indent}  </div>`)
      }

      colLines.push(`${indent}</div>`)
      return colLines.join('\n')
    }
    case 'subform': {
      const sfCols = block.subformColumns || [
        { label: 'Column 1', type: 'text' },
        { label: 'Column 2', type: 'text' },
      ]
      const sfMin = block.subformMinRows || 1
      const sfMax = block.subformMaxRows || 10
      const sfShowName = block.subformShowName !== false
      const sfName = esc(block.subformName || block.name || 'subform')
      const sfLines: string[] = []
      sfLines.push(
        `${indent}<div class="fb-subform" ${wrapperAttr} data-sf-name="${esc(block.name)}" data-sf-min="${sfMin}" data-sf-max="${sfMax}">`,
      )
      if (sfShowName) {
        sfLines.push(
          `${indent}  <div class="fb-subform-header"><span class="fb-subform-title">${sfName}</span></div>`,
        )
      }
      sfLines.push(`${indent}  <table class="fb-subform-table">`)
      sfLines.push(`${indent}    <thead><tr>`)
      sfLines.push(`${indent}      <th class="fb-sf-th">#</th>`)
      for (const col of sfCols) {
        sfLines.push(
          `${indent}      <th class="fb-sf-th">${esc(col.label)}${col.required ? '<span class="fb-required">*</span>' : ''}</th>`,
        )
      }
      sfLines.push(`${indent}      <th class="fb-sf-th fb-sf-actions"></th>`)
      sfLines.push(`${indent}    </tr></thead>`)
      sfLines.push(`${indent}    <tbody class="fb-sf-body">`)
      // Render initial rows based on minRows
      for (let r = 0; r < sfMin; r++) {
        sfLines.push(`${indent}      <tr class="fb-sf-row">`)
        sfLines.push(
          `${indent}        <td class="fb-sf-td fb-sf-num">${r + 1}</td>`,
        )
        for (const col of sfCols) {
          const fieldName = `${esc(block.name)}[${r}][${esc(col.label.toLowerCase().replace(/\s+/g, '_'))}]`
          const reqAttr = col.required ? ' required' : ''
          const phAttr = col.placeholder
            ? ` placeholder="${esc(col.placeholder)}"`
            : ''
          if (col.type === 'select') {
            sfLines.push(
              `${indent}        <td class="fb-sf-td"><select name="${fieldName}" class="fb-input fb-sf-input"${reqAttr}>`,
            )
            sfLines.push(
              `${indent}          <option value="">${col.placeholder || 'Select...'}</option>`,
            )
            for (const opt of col.options || []) {
              sfLines.push(
                `${indent}          <option value="${esc(opt.value)}">${esc(opt.label)}</option>`,
              )
            }
            sfLines.push(`${indent}        </select></td>`)
          } else if (col.type === 'textarea') {
            sfLines.push(
              `${indent}        <td class="fb-sf-td"><textarea name="${fieldName}" class="fb-input fb-sf-input" rows="2"${reqAttr}${phAttr}></textarea></td>`,
            )
          } else {
            const inputType =
              col.type === 'number'
                ? 'number'
                : col.type === 'email'
                  ? 'email'
                  : col.type === 'date'
                    ? 'date'
                    : col.type === 'url'
                      ? 'url'
                      : 'text'
            sfLines.push(
              `${indent}        <td class="fb-sf-td"><input type="${inputType}" name="${fieldName}" class="fb-input fb-sf-input"${reqAttr}${phAttr} /></td>`,
            )
          }
        }
        sfLines.push(
          `${indent}        <td class="fb-sf-td fb-sf-actions"><button type="button" class="fb-sf-remove" onclick="this.closest('.fb-sf-row').remove();(function(t){var rows=t.querySelectorAll('.fb-sf-row');for(var i=0;i<rows.length;i++)rows[i].querySelector('.fb-sf-num').textContent=i+1;})(this.closest('.fb-subform'));" title="Remove row">✕</button></td>`,
        )
        sfLines.push(`${indent}      </tr>`)
      }
      sfLines.push(`${indent}    </tbody>`)
      sfLines.push(`${indent}  </table>`)
      sfLines.push(
        `${indent}  <button type="button" class="fb-sf-add" onclick="var sf=this.closest('.fb-subform');var tbody=sf.querySelector('.fb-sf-body');var max=parseInt(sf.dataset.sfMax)||100;if(tbody.children.length>=max){alert('Maximum '+max+' rows allowed');return;}var first=tbody.querySelector('.fb-sf-row');if(!first)return;var clone=first.cloneNode(true);var inputs=clone.querySelectorAll('input,select,textarea');for(var i=0;i<inputs.length;i++){inputs[i].value='';var n=inputs[i].name;if(n)inputs[i].name=n.replace(/\\[\\d+\\]/,'['+(tbody.children.length)+']');}clone.querySelector('.fb-sf-num').textContent=tbody.children.length+1;tbody.appendChild(clone);">+ Add Row</button>`,
      )
      sfLines.push(`${indent}</div>`)
      return sfLines.join('\n')
    }
    default:
      return ''
  }
}

function renderInput(
  block: FormBlock,
  indent: string,
  wrapClass: string,
  wrapperAttr: string,
): string {
  const typeMap: Record<string, string> = {
    text: 'text',
    email: 'email',
    number: 'number',
    url: 'url',
    date: 'date',
  }
  const inputType = typeMap[block.type] || 'text'
  const attrs: string[] = [
    `type="${inputType}"`,
    `name="${esc(block.name)}"`,
    `id="${esc(block.name)}"`,
  ]
  if (block.placeholder) attrs.push(`placeholder="${esc(block.placeholder)}"`)
  if (block.required) attrs.push('required')
  if (block.defaultValue) attrs.push(`value="${esc(block.defaultValue)}"`)
  if (block.validation.minLength)
    attrs.push(`minlength="${block.validation.minLength}"`)
  if (block.validation.maxLength)
    attrs.push(`maxlength="${block.validation.maxLength}"`)
  if (block.validation.min !== undefined)
    attrs.push(`min="${block.validation.min}"`)
  if (block.validation.max !== undefined)
    attrs.push(`max="${block.validation.max}"`)
  if (block.validation.pattern)
    attrs.push(`pattern="${esc(block.validation.pattern)}"`)

  const lines = [
    `${indent}<div class="${wrapClass}" ${wrapperAttr}>`,
    `${indent}  <label for="${esc(block.name)}" class="fb-label">${esc(block.label)}${block.required ? ' <span class="fb-required">*</span>' : ''}</label>`,
    `${indent}  <input ${attrs.join(' ')} class="fb-input" />`,
  ]
  if (block.helpText)
    lines.push(`${indent}  <span class="fb-help">${esc(block.helpText)}</span>`)
  lines.push(`${indent}</div>`)
  return lines.join('\n')
}

function renderPhoneInput(
  block: FormBlock,
  indent: string,
  wrapClass: string,
  wrapperAttr: string,
): string {
  const defaultCountryIso = block.phoneDefaultCountry || 'US'
  const defaultCountry =
    COUNTRY_CODES.find((c) => c.iso === defaultCountryIso) ||
    COUNTRY_CODES.find((c) => c.iso === 'US')!

  const attrs: string[] = [`type="tel"`, `id="${esc(block.name)}_number"`]
  if (block.placeholder) attrs.push(`placeholder="${esc(block.placeholder)}"`)
  if (block.required) attrs.push('required')
  if (block.defaultValue) attrs.push(`value="${esc(block.defaultValue)}"`)

  const lines = [
    `${indent}<div class="${wrapClass} fb-phone-field" ${wrapperAttr}>`,
    `${indent}  <label for="${esc(block.name)}_number" class="fb-label">${esc(block.label)}${block.required ? ' <span class="fb-required">*</span>' : ''}</label>`,
    `${indent}  <div class="fb-phone-wrapper">`,
    `${indent}    <div class="fb-phone-dropdown" id="dropdown_${esc(block.name)}">`,
    `${indent}      <button type="button" class="fb-phone-toggle" id="toggle_${esc(block.name)}">`,
    `${indent}        <span class="fb-phone-flag" id="flag_${esc(block.name)}">${defaultCountry.flag}</span>`,
    `${indent}        <span class="fb-phone-dial" id="dial_${esc(block.name)}">${defaultCountry.dialCode}</span>`,
    `${indent}        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
    `${indent}      </button>`,
    `${indent}      <div class="fb-phone-menu" id="menu_${esc(block.name)}">`,
    `${indent}        <div class="fb-phone-search-wrapper">`,
    `${indent}          <input type="text" class="fb-phone-search" id="search_${esc(block.name)}" placeholder="Search country...">`,
    `${indent}        </div>`,
    `${indent}        <ul class="fb-phone-list" id="list_${esc(block.name)}">`,
  ]

  for (const country of COUNTRY_CODES) {
    lines.push(
      `${indent}          <li class="fb-phone-option" data-iso="${country.iso}" data-dial="${country.dialCode}" data-name="${esc(country.name)}">`,
    )
    lines.push(
      `${indent}            <span class="fb-phone-opt-flag">${country.flag}</span>`,
    )
    lines.push(
      `${indent}            <span class="fb-phone-opt-name">${esc(country.name)}</span>`,
    )
    lines.push(
      `${indent}            <span class="fb-phone-opt-dial">${country.dialCode}</span>`,
    )
    lines.push(`${indent}          </li>`)
  }

  lines.push(`${indent}        </ul>`)
  lines.push(`${indent}      </div>`)
  lines.push(`${indent}    </div>`)
  lines.push(
    `${indent}    <input ${attrs.join(' ')} class="fb-input fb-phone-input" />`,
  )
  lines.push(
    `${indent}    <input type="hidden" name="${esc(block.name)}" id="${esc(block.name)}" value="${defaultCountry.dialCode}${block.defaultValue ? ' ' + block.defaultValue : ''}" />`,
  )
  lines.push(`${indent}  </div>`)
  if (block.helpText)
    lines.push(`${indent}  <span class="fb-help">${esc(block.helpText)}</span>`)
  lines.push(`${indent}</div>`)

  return lines.join('\n')
}

function renderPhoneField(block: FormBlock, theme: FormTheme): string {
  const defaultCountryIso = block.phoneDefaultCountry || 'US'
  const defaultCountry =
    COUNTRY_CODES.find((c) => c.iso === defaultCountryIso) ||
    COUNTRY_CODES.find((c) => c.iso === 'US')!

  const attrs: string[] = [`type="tel"`, `id="${esc(block.name)}_number"`]
  if (block.placeholder) attrs.push(`placeholder="${esc(block.placeholder)}"`)
  if (block.required) attrs.push('required')
  if (block.defaultValue) attrs.push(`value="${esc(block.defaultValue)}"`)

  const lines = [
    `${indent}<div class="fb-phone-field" ${wrapperAttr}>`,
    `${indent}  <label for="${esc(block.name)}_number" class="fb-label">${esc(block.label)}${block.required ? ' <span class="fb-required">*</span>' : ''}</label>`,
    `${indent}  <div class="fb-phone-wrapper">`,
    `${indent}    <div class="fb-phone-dropdown" id="dropdown_${esc(block.name)}">`,
    `${indent}      <button type="button" class="fb-phone-toggle" id="toggle_${esc(block.name)}">`,
    `${indent}        <span class="fb-phone-flag" id="flag_${esc(block.name)}">${defaultCountry.flag}</span>`,
    `${indent}        <span class="fb-phone-dial" id="dial_${esc(block.name)}">${defaultCountry.dialCode}</span>`,
    `${indent}        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
    `${indent}      </button>`,
    `${indent}      <div class="fb-phone-menu" id="menu_${esc(block.name)}">`,
    `${indent}        <div class="fb-phone-search-wrapper">`,
    `${indent}          <input type="text" class="fb-phone-search" id="search_${esc(block.name)}" placeholder="Search country...">`,
    `${indent}        </div>`,
    `${indent}        <ul class="fb-phone-list" id="list_${esc(block.name)}">`,
  ]

  for (const country of COUNTRY_CODES) {
    lines.push(
      `${indent}          <li class="fb-phone-option" data-iso="${country.iso}" data-dial="${country.dialCode}" data-name="${esc(country.name)}">`,
    )
    lines.push(
      `${indent}            <span class="fb-phone-opt-flag">${country.flag}</span>`,
    )
    lines.push(
      `${indent}            <span class="fb-phone-opt-name">${esc(country.name)}</span>`,
    )
    lines.push(
      `${indent}            <span class="fb-phone-opt-dial">${country.dialCode}</span>`,
    )
    lines.push(`${indent}          </li>`)
  }

  lines.push(`${indent}        </ul>`)
  lines.push(`${indent}      </div>`)
  lines.push(`${indent}    </div>`)
  lines.push(
    `${indent}    <input ${attrs.join(' ')} class="fb-input fb-phone-input" />`,
  )
  lines.push(
    `${indent}    <input type="hidden" name="${esc(block.name)}" id="${esc(block.name)}" value="${defaultCountry.dialCode}${block.defaultValue ? ' ' + block.defaultValue : ''}" />`,
  )
  lines.push(`${indent}  </div>`)
  if (block.helpText)
    lines.push(`${indent}  <span class="fb-help">${esc(block.helpText)}</span>`)
  lines.push(`${indent}</div>`)

  return lines.join('\n')
}

function renderTextarea(
  block: FormBlock,
  indent: string,
  wrapClass: string,
  wrapperAttr: string,
): string {
  const attrs: string[] = [
    `name="${esc(block.name)}"`,
    `id="${esc(block.name)}"`,
  ]
  if (block.placeholder) attrs.push(`placeholder="${esc(block.placeholder)}"`)
  if (block.required) attrs.push('required')
  if (block.validation.minLength)
    attrs.push(`minlength="${block.validation.minLength}"`)
  if (block.validation.maxLength)
    attrs.push(`maxlength="${block.validation.maxLength}"`)

  const lines = [
    `${indent}<div class="${wrapClass}" ${wrapperAttr}>`,
    `${indent}  <label for="${esc(block.name)}" class="fb-label">${esc(block.label)}${block.required ? ' <span class="fb-required">*</span>' : ''}</label>`,
    `${indent}  <textarea ${attrs.join(' ')} rows="4" class="fb-input fb-textarea">${esc(block.defaultValue)}</textarea>`,
  ]
  if (block.helpText)
    lines.push(`${indent}  <span class="fb-help">${esc(block.helpText)}</span>`)
  lines.push(`${indent}</div>`)
  return lines.join('\n')
}

function renderSelect(
  block: FormBlock,
  indent: string,
  wrapClass: string,
  wrapperAttr: string,
): string {
  const options = block.validation.options || []
  const lines = [
    `${indent}<div class="${wrapClass}" ${wrapperAttr}>`,
    `${indent}  <label for="${esc(block.name)}" class="fb-label">${esc(block.label)}${block.required ? ' <span class="fb-required">*</span>' : ''}</label>`,
    `${indent}  <select name="${esc(block.name)}" id="${esc(block.name)}" class="fb-input fb-select"${block.required ? ' required' : ''}>`,
    `${indent}    <option value="">${block.placeholder || 'Select...'}</option>`,
  ]
  for (const opt of options) {
    lines.push(
      `${indent}    <option value="${esc(opt.value)}">${esc(opt.label)}</option>`,
    )
  }
  lines.push(`${indent}  </select>`)
  if (block.helpText)
    lines.push(`${indent}  <span class="fb-help">${esc(block.helpText)}</span>`)
  lines.push(`${indent}</div>`)
  return lines.join('\n')
}

function renderRadioGroup(
  block: FormBlock,
  indent: string,
  wrapClass: string,
  wrapperAttr: string,
): string {
  const options = block.validation.options || []
  const isHorizontal = block.optionsLayout === 'horizontal'
  const cols = block.optionsColumns || 1
  const containerClass = isHorizontal
    ? 'fb-options-inline'
    : cols > 1
      ? `fb-options-grid fb-options-cols-${cols}`
      : ''
  const lines = [
    `${indent}<div class="${wrapClass}" ${wrapperAttr}>`,
    `${indent}  <fieldset class="fb-fieldset">`,
    `${indent}    <legend class="fb-label">${esc(block.label)}${block.required ? ' <span class="fb-required">*</span>' : ''}</legend>`,
    containerClass ? `${indent}    <div class="${containerClass}">` : '',
  ].filter(Boolean)
  for (const opt of options) {
    const optId = `${block.name}_${opt.value}`
    lines.push(
      `${indent}    <label class="fb-option"><input type="radio" name="${esc(block.name)}" value="${esc(opt.value)}" id="${esc(optId)}"${block.required ? ' required' : ''} /> ${esc(opt.label)}</label>`,
    )
  }
  if (containerClass) lines.push(`${indent}    </div>`)
  lines.push(`${indent}  </fieldset>`)
  if (block.helpText)
    lines.push(`${indent}  <span class="fb-help">${esc(block.helpText)}</span>`)
  lines.push(`${indent}</div>`)
  return lines.join('\n')
}

function renderCheckboxGroup(
  block: FormBlock,
  indent: string,
  wrapClass: string,
  wrapperAttr: string,
): string {
  const options = block.validation.options || []
  const isHorizontal = block.optionsLayout === 'horizontal'
  const cols = block.optionsColumns || 1
  const containerClass = isHorizontal
    ? 'fb-options-inline'
    : cols > 1
      ? `fb-options-grid fb-options-cols-${cols}`
      : ''
  const lines = [
    `${indent}<div class="${wrapClass}" ${wrapperAttr}>`,
    `${indent}  <fieldset class="fb-fieldset">`,
    `${indent}    <legend class="fb-label">${esc(block.label)}${block.required ? ' <span class="fb-required">*</span>' : ''}</legend>`,
    containerClass ? `${indent}    <div class="${containerClass}">` : '',
  ].filter(Boolean)
  for (const opt of options) {
    const optId = `${block.name}_${opt.value}`
    lines.push(
      `${indent}    <label class="fb-option"><input type="checkbox" name="${esc(block.name)}" value="${esc(opt.value)}" id="${esc(optId)}" /> ${esc(opt.label)}</label>`,
    )
  }
  if (containerClass) lines.push(`${indent}    </div>`)
  lines.push(`${indent}  </fieldset>`)
  if (block.helpText)
    lines.push(`${indent}  <span class="fb-help">${esc(block.helpText)}</span>`)
  lines.push(`${indent}</div>`)
  return lines.join('\n')
}

function renderConsent(
  block: FormBlock,
  indent: string,
  wrapClass: string,
  wrapperAttr: string,
): string {
  const text = block.consentText || block.label || 'I agree'
  const lines = [
    `${indent}<div class="${wrapClass} fb-consent" ${wrapperAttr}>`,
    `${indent}  <label class="fb-option fb-consent-label">`,
    `${indent}    <input type="checkbox" name="${esc(block.name)}" value="yes"${block.required ? ' required' : ''} id="${esc(block.name)}" />`,
    `${indent}    <span class="fb-consent-text">${esc(text)}${block.required ? ' <span class="fb-required">*</span>' : ''}</span>`,
    `${indent}  </label>`,
  ]
  if (block.helpText)
    lines.push(`${indent}  <span class="fb-help">${esc(block.helpText)}</span>`)
  lines.push(`${indent}</div>`)
  return lines.join('\n')
}

function renderFileInput(
  block: FormBlock,
  indent: string,
  wrapClass: string,
  wrapperAttr: string,
): string {
  const attrs: string[] = [
    `type="file"`,
    `name="${esc(block.name)}"`,
    `id="${esc(block.name)}"`,
  ]
  if (block.required) attrs.push('required')
  if (block.validation.accept)
    attrs.push(`accept="${esc(block.validation.accept)}"`)

  const lines = [
    `${indent}<div class="${wrapClass}" ${wrapperAttr}>`,
    `${indent}  <label for="${esc(block.name)}" class="fb-label">${esc(block.label)}${block.required ? ' <span class="fb-required">*</span>' : ''}</label>`,
    `${indent}  <input ${attrs.join(' ')} class="fb-input fb-file" />`,
  ]
  if (block.helpText)
    lines.push(`${indent}  <span class="fb-help">${esc(block.helpText)}</span>`)
  if (block.validation.maxFileSize)
    lines.push(
      `${indent}  <span class="fb-help">Max file size: ${block.validation.maxFileSize}MB</span>`,
    )
  lines.push(`${indent}</div>`)
  return lines.join('\n')
}

function renderSubmitButton(block: FormBlock, theme: FormTheme): string {
  const align = block.buttonAlign || 'full'
  const submitStyles: string[] = []
  const btnInlineStyles: string[] = []
  if (block.buttonColor) {
    btnInlineStyles.push(`background:${esc(block.buttonColor)}`)
    btnInlineStyles.push(`border-color:${esc(block.buttonColor)}`)
  }
  // Position behavior
  if (block.buttonPosition === 'sticky') {
    submitStyles.push('position:sticky')
    submitStyles.push(`bottom:${block.buttonStickyOffset || 0}px`)
    submitStyles.push('z-index:50')
    submitStyles.push('background:inherit')
    submitStyles.push('padding:12px 0')
  } else if (block.buttonPosition === 'fixed') {
    submitStyles.push('position:fixed')
    submitStyles.push(`bottom:${block.buttonStickyOffset || 0}px`)
    submitStyles.push('left:0')
    submitStyles.push('right:0')
    submitStyles.push('z-index:1000')
    submitStyles.push('padding:12px 24px')
    submitStyles.push('background:rgba(255,255,255,0.95)')
    submitStyles.push('backdrop-filter:blur(8px)')
    submitStyles.push('box-shadow:0 -2px 10px rgba(0,0,0,0.1)')
  }
  const submitStyleAttr = submitStyles.length
    ? ` style="${submitStyles.join(';')}"`
    : ''
  const btnStyleAttr = btnInlineStyles.length
    ? ` style="${btnInlineStyles.join(';')}"`
    : ''
  return `${indent}<div class="fb-submit fb-submit-${align}"${submitStyleAttr} ${wrapperAttr}><button type="submit" class="fb-button"${btnStyleAttr}>${esc(block.buttonText || 'Submit')}</button></div>`
}

function shadowValue(size: 'none' | 'sm' | 'md' | 'lg'): string {
  switch (size) {
    case 'sm':
      return '0 1px 2px 0 rgba(0,0,0,0.05)'
    case 'md':
      return '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)'
    case 'lg':
      return '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)'
    default:
      return 'none'
  }
}

function generateFormCss(theme: FormTheme): string {
  const bg =
    theme.backgroundType === 'gradient'
      ? `linear-gradient(${theme.gradientDirection}, ${theme.gradientFrom}, ${theme.gradientTo})`
      : theme.backgroundColor

  const containerShadow = shadowValue(theme.containerShadow)
  const containerBorderCss = theme.containerBorder
    ? `border: 1px solid ${theme.inputBorderColor};`
    : ''
  const inputShadowCss = theme.inputShadow
    ? 'box-shadow: 0 1px 3px 0 rgba(0,0,0,0.08);'
    : ''
  const buttonShadowCss = theme.buttonShadow
    ? 'box-shadow: 0 4px 14px 0 rgba(0,0,0,0.15);'
    : ''
  const focusColor = theme.primaryColor || '#3b82f6'

  let css = `
.fb-form {
  font-family: ${theme.fontFamily};
  max-width: ${theme.cardWidth || 720}px;
  margin: 0 auto;
  padding: ${theme.formPadding || 32}px;
  background: ${bg};
  border-radius: ${theme.borderRadius + 4}px;
  box-shadow: ${containerShadow};
  ${containerBorderCss}
}
.fb-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
@media (max-width: 480px) {
  .fb-row { grid-template-columns: 1fr; }
}
.fb-field {
  margin-bottom: ${theme.fieldSpacing || 20}px;
}
.fb-label {
  display: block;
  font-size: ${(theme.labelFontSize || 14) / 16}rem;
  font-weight: ${theme.labelFontWeight || '500'};
  color: ${theme.labelColor};
  margin-bottom: 0.375rem;
}
.fb-required {
  color: #ef4444;
}
.fb-input {
  display: block;
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: #111827;
  background: ${theme.inputBackground || '#fff'};
  border: 1px solid ${theme.inputBorderColor};
  border-radius: ${theme.borderRadius}px;
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;
  ${inputShadowCss}
}
.fb-input:focus {
  outline: none;
  border-color: ${focusColor};
  box-shadow: 0 0 0 3px ${focusColor}22;
}
.fb-input-error {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 3px rgba(239,68,68,0.15) !important;
}
.fb-phone-wrapper {
  display: flex;
  align-items: stretch;
  border: 1px solid ${theme.inputBorderColor};
  border-radius: ${theme.borderRadius}px;
  background: ${theme.inputBackground || '#fff'};
  transition: border-color 0.15s, box-shadow 0.15s;
  ${inputShadowCss}
}
.fb-phone-wrapper:focus-within {
  border-color: ${focusColor};
  box-shadow: 0 0 0 3px ${focusColor}22;
}
.fb-phone-dropdown {
  position: relative;
  display: flex;
}
.fb-phone-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  background: transparent;
  border: none;
  border-right: 1px solid ${theme.inputBorderColor};
  cursor: pointer;
  font-size: 0.875rem;
  color: #4b5563;
  border-radius: ${theme.borderRadius}px 0 0 ${theme.borderRadius}px;
}
.fb-phone-toggle:hover {
  background: rgba(0,0,0,0.02);
}
.fb-phone-toggle svg {
  color: #9ca3af;
}
.fb-phone-menu {
  display: none;
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 280px;
  max-width: calc(100vw - 2rem);
  max-height: 300px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1);
  z-index: 50;
  flex-direction: column;
  overflow: hidden;
}
.fb-phone-menu.open {
  display: flex;
}
.fb-phone-search-wrapper {
  padding: 8px;
  border-bottom: 1px solid #e5e7eb;
}
.fb-phone-search {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  font-size: 0.875rem;
  outline: none;
}
.fb-phone-search:focus {
  border-color: ${focusColor};
}
.fb-phone-list {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  flex: 1;
}
.fb-phone-option {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 0.875rem;
}
.fb-phone-option:hover {
  background: #f3f4f6;
}
.fb-phone-opt-flag {
  margin-right: 8px;
  font-size: 1.1em;
}
.fb-phone-opt-name {
  flex: 1;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 8px;
}
.fb-phone-opt-dial {
  color: #6b7280;
  font-variant-numeric: tabular-nums;
}
.fb-phone-input {
  flex: 1;
  border: none !important;
  box-shadow: none !important;
  border-radius: 0 ${theme.borderRadius}px ${theme.borderRadius}px 0 !important;
  background: transparent !important;
}
.fb-phone-input:focus {
  box-shadow: none !important;
}
.fb-textarea {
  resize: vertical;
  min-height: 100px;
}
.fb-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M2 4l4 4 4-4'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  padding-right: 2rem;
}
.fb-fieldset {
  border: none;
  padding: 0;
  margin: 0;
}
.fb-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
  padding: 0.25rem 0;
  cursor: pointer;
}
.fb-option input {
  accent-color: ${theme.primaryColor};
}
.fb-options-inline {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1rem;
}
.fb-options-inline .fb-option {
  flex-shrink: 0;
}
.fb-options-grid {
  display: grid;
  gap: 0.25rem 1rem;
}
.fb-options-cols-2 { grid-template-columns: repeat(2, 1fr); }
.fb-options-cols-3 { grid-template-columns: repeat(3, 1fr); }
.fb-options-cols-4 { grid-template-columns: repeat(4, 1fr); }
.fb-consent-label {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  cursor: pointer;
}
.fb-consent-label input[type="checkbox"] {
  margin-top: 0.2rem;
  accent-color: ${theme.primaryColor};
  flex-shrink: 0;
}
.fb-consent-text {
  font-size: 0.875rem;
  color: ${theme.labelColor};
  line-height: 1.4;
}
.fb-help {
  display: block;
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 0.25rem;
}
.fb-heading {
  color: ${theme.headingColor || '#111827'};
  margin: 0 0 0.5rem;
}
.fb-paragraph {
  color: ${theme.paragraphColor || '#6b7280'};
  font-size: 0.875rem;
  line-height: 1.6;
  margin: 0 0 1rem;
}
.fb-divider {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 1rem 0;
}
.fb-spacer {
  display: block;
}
.fb-image {
  max-width: 100%;
  height: auto;
  border-radius: ${theme.borderRadius}px;
}
.fb-image-placeholder {
  background: #f3f4f6;
  border: 2px dashed #d1d5db;
  border-radius: ${theme.borderRadius}px;
  padding: 2rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
}
.fb-submit {
  margin-top: 1.5rem;
}
.fb-submit-center { text-align: center; }
.fb-submit-right { text-align: right; }
.fb-submit-full .fb-button { width: 100%; }
.fb-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: ${theme.borderRadius}px;
  cursor: pointer;
  transition: background-color 0.15s, opacity 0.15s, transform 0.1s;
  border: ${theme.buttonStyle === 'outline' ? `2px solid ${theme.primaryColor}` : 'none'};
  background: ${theme.buttonStyle === 'outline' ? 'transparent' : theme.primaryColor};
  color: ${theme.buttonStyle === 'outline' ? theme.primaryColor : theme.buttonTextColor || '#ffffff'};
  ${buttonShadowCss}
}
.fb-button:hover {
  opacity: 0.9;
  transform: translateY(-1px);
}
.fb-button:active {
  transform: translateY(0);
}
.fb-file {
  padding: 0.375rem;
}
.fb-file::file-selector-button {
  padding: 0.375rem 0.75rem;
  border-radius: ${theme.borderRadius}px;
  border: 1px solid ${theme.inputBorderColor};
  background: #f9fafb;
  font-size: 0.8125rem;
  cursor: pointer;
  margin-right: 0.75rem;
}
/* Image animations */
.fb-image-wrap {
  transition: opacity 0.3s;
}
@keyframes fbFadeInImg {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes fbSlideUp {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes fbSlideDown {
  from { opacity: 0; transform: translateY(-30px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes fbZoomIn {
  from { opacity: 0; transform: scale(0.7); }
  to { opacity: 1; transform: scale(1); }
}
@keyframes fbBlurIn {
  from { opacity: 0; filter: blur(12px); }
  to { opacity: 1; filter: blur(0); }
}
.fb-img-fadeIn { animation: fbFadeInImg 0.8s ease both; }
.fb-img-slideUp { animation: fbSlideUp 0.7s ease both; }
.fb-img-slideDown { animation: fbSlideDown 0.7s ease both; }
.fb-img-zoomIn { animation: fbZoomIn 0.6s ease both; }
.fb-img-blurIn { animation: fbBlurIn 0.9s ease both; }
/* Sections */
.fb-section {
  border: 1px solid #e5e7eb;
  border-radius: ${theme.borderRadius + 2}px;
  margin-bottom: ${theme.fieldSpacing || 20}px;
  overflow: hidden;
}
.fb-section-noborder { border: none; }
.fb-section-dashed { border-style: dashed; }
.fb-section-accent { border-left: 4px solid ${theme.primaryColor}; }
.fb-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 1rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}
.fb-section-collapsible { cursor: pointer; user-select: none; }
.fb-section-collapsible:hover { background: #f3f4f6; }
.fb-section-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${theme.headingColor || '#111827'};
  letter-spacing: 0.01em;
}
.fb-section-chevron {
  font-size: 0.75rem;
  color: #9ca3af;
  transition: transform 0.2s;
}
.fb-section-collapsed .fb-section-chevron { transform: rotate(-90deg); }
.fb-section-collapsed .fb-section-body {
  display: none;
}
.fb-section-body {
  padding: 1rem;
}
/* Columns */
.fb-columns {
  display: grid;
  grid-template-columns: repeat(var(--col-count, 2), 1fr);
  gap: var(--col-gap, 16px);
  margin-bottom: ${theme.fieldSpacing || 20}px;
}
.fb-col {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
@media (max-width: 640px) {
  .fb-columns {
    grid-template-columns: 1fr !important;
  }
}
/* Section scroll animations */
@keyframes fbSecFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes fbSecSlideLeft {
  from { opacity: 0; transform: translateX(-40px); }
  to { opacity: 1; transform: translateX(0); }
}
@keyframes fbSecSlideRight {
  from { opacity: 0; transform: translateX(40px); }
  to { opacity: 1; transform: translateX(0); }
}
@keyframes fbSecSlideUp {
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes fbSecSlideDown {
  from { opacity: 0; transform: translateY(-40px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes fbSecZoomIn {
  from { opacity: 0; transform: scale(0.85); }
  to { opacity: 1; transform: scale(1); }
}
.fb-sec-fadeIn { animation: fbSecFadeIn 0.7s ease both; }
.fb-sec-slideLeft { animation: fbSecSlideLeft 0.6s ease both; }
.fb-sec-slideRight { animation: fbSecSlideRight 0.6s ease both; }
.fb-sec-slideUp { animation: fbSecSlideUp 0.6s ease both; }
.fb-sec-slideDown { animation: fbSecSlideDown 0.6s ease both; }
.fb-sec-zoomIn { animation: fbSecZoomIn 0.5s ease both; }
/* Subforms */
.fb-subform {
  border: 1px solid #e5e7eb;
  border-radius: ${theme.borderRadius + 2}px;
  margin-bottom: ${theme.fieldSpacing || 20}px;
  overflow: hidden;
}
.fb-subform-header {
  display: flex;
  align-items: center;
  padding: 0.625rem 1rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}
.fb-subform-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${theme.headingColor || '#111827'};
}
.fb-subform-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
}
.fb-sf-th {
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  text-align: left;
}
.fb-sf-td {
  padding: 0.375rem 0.75rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}
.fb-sf-num {
  color: #9ca3af;
  font-size: 0.75rem;
  width: 2rem;
  text-align: center;
}
.fb-sf-input {
  width: 100%;
  padding: 0.375rem 0.5rem;
  font-size: 0.8125rem;
  border: 1px solid ${theme.inputBorderColor};
  border-radius: ${theme.borderRadius}px;
  background: ${theme.inputBackground || '#ffffff'};
}
.fb-sf-input:focus {
  outline: none;
  border-color: ${theme.primaryColor};
  box-shadow: 0 0 0 2px ${theme.primaryColor}22;
}
.fb-sf-actions {
  width: 2rem;
  text-align: center;
}
.fb-sf-remove {
  background: none;
  border: none;
  color: #d1d5db;
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0.25rem;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;
}
.fb-sf-remove:hover {
  color: #ef4444;
  background: #fef2f2;
}
.fb-sf-add {
  display: block;
  width: 100%;
  padding: 0.5rem;
  background: none;
  border: none;
  border-top: 1px solid #f3f4f6;
  color: ${theme.primaryColor};
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}
.fb-sf-add:hover {
  background: #f9fafb;
}
.fb-required {
  color: #ef4444;
  margin-left: 2px;
}`

  // Card mode CSS
  if (theme.layoutMode === 'card') {
    css += `
/* Card Mode */
.fb-card-wrapper {
  font-family: ${theme.fontFamily};
  max-width: ${theme.cardWidth || 720}px;
  margin: 0 auto;
  min-height: 400px;
  display: flex;
  flex-direction: column;
}
.fb-card-progress {
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 1rem;
}
.fb-card-progress-bar {
  height: 100%;
  background: ${theme.primaryColor};
  border-radius: 2px;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  width: 10%;
}
.fb-card-counter {
  text-align: center;
  font-size: 0.75rem;
  color: #9ca3af;
  margin-bottom: 1.5rem;
  font-family: ${theme.fontFamily};
  letter-spacing: 0.025em;
}
.fb-card-mode {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
}
.fb-step {
  display: none;
  flex-direction: column;
  flex: 1;${theme.cardTransition === 'fade' ? '\n  animation: fbFadeIn 0.35s ease;' : theme.cardTransition === 'slide' ? '\n  animation: fbSlideIn 0.35s ease;' : ''}
}
.fb-step-active {
  display: flex !important;
}
@keyframes fbFadeIn {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes fbSlideIn {
  from { opacity: 0; transform: translateX(30px); }
  to { opacity: 1; transform: translateX(0); }
}
.fb-step-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2rem 0;
}
.fb-step-content .fb-field {
  margin-bottom: 1rem;
}
.fb-step-content .fb-label {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: ${theme.headingColor || '#111827'};
}
.fb-step-content .fb-input {
  font-size: 1rem;
  padding: 0.75rem 1rem;
}
.fb-step-content .fb-option {
  font-size: 1rem;
  padding: 0.5rem 0;
}
.fb-section-title {
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${theme.primaryColor};
  margin-bottom: 0.5rem;
}
.fb-card-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1.5rem;
  margin-top: auto;
}
.fb-nav-btn {
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: ${theme.borderRadius}px;
  cursor: pointer;
  transition: all 0.15s;
  font-family: ${theme.fontFamily};
}
.fb-nav-prev {
  background: transparent;
  border: 1px solid #e5e7eb;
  color: #6b7280;
}
.fb-nav-prev:hover {
  background: #f9fafb;
  border-color: #d1d5db;
  color: #374151;
}
.fb-nav-next {
  background: ${theme.primaryColor};
  border: none;
  color: ${theme.buttonTextColor || '#ffffff'};
}
.fb-nav-next:hover {
  opacity: 0.9;
}
/* Pills nav style */
.fb-card-nav-pills .fb-nav-btn {
  border-radius: 999px;
  padding: 0.5rem 1.5rem;
}
.fb-card-nav-pills .fb-nav-prev {
  background: #f3f4f6;
  border: none;
  color: #374151;
}
.fb-card-nav-pills .fb-nav-prev:hover {
  background: #e5e7eb;
}
.fb-card-nav-pills .fb-nav-next {
  box-shadow: 0 2px 8px ${theme.primaryColor}33;
}
/* Icons nav style */
.fb-card-nav-icons .fb-nav-btn {
  width: 40px;
  height: 40px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 1.25rem;
  line-height: 1;
}
.fb-card-nav-icons .fb-nav-prev {
  background: #f3f4f6;
  border: none;
  color: #374151;
}
.fb-card-nav-icons .fb-nav-prev:hover {
  background: #e5e7eb;
}
.fb-card-nav-icons .fb-nav-next {
  box-shadow: 0 2px 8px ${theme.primaryColor}33;
}
.fb-input-error {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 2px rgba(239,68,68,0.15) !important;
}`
  }

  return css
}

function esc(str?: string | number): string {
  if (str === undefined || str === null) return ''
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export default generateFormHtml
